/**
 * This file is part of the Haiman.
 * @link     : https://haiman.io/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : hightman (hightman@cloud-sun.com)
 * @copyright: Copyright (c) 2018 Hangzhou Haila Network Technology Co., Ltd.
 */

import * as api from '@/api/secret';
import { SECRET } from '@/store/types';
import { StoreActionEnv } from '@/store/actions';
import { SecretEmotion } from '@/api/types/secret';
import navModule, { StoreSecretNavState } from './nav';
import filterModule, { StoreSecretFilterState } from './filter';
import listModule, { StoreSecretListState } from './list';
import postModule, { StoreSecretPostState } from './post';
import searchModule, { StoreSecretSearchState } from './search';
import commentModule, { StoreSecretCommentState } from './comment';
import topsModule, { StoreSecretTopsState } from './tops';
import luckyModule, { StoreSecretLuckyState } from './lucky';
import topicModule, { StoreSecretTopicState } from './topic';
import privateRoomsModule, { StorePrivateRoomsState } from './private-rooms';
import roomModule, { StoreSecretRoomState } from './room';

export interface StoreSecretState {
  emotions: SecretEmotion[];
  pendings: { resolve: () => void; reject: (err: Error) => void }[];
  inputTips: Record<string, string[]>;
  stickyRules: string[];
  // modules
  nav: StoreSecretNavState;
  filter: StoreSecretFilterState;
  list: StoreSecretListState;
  post: StoreSecretPostState;
  search: StoreSecretSearchState;
  comment: StoreSecretCommentState;
  tops: StoreSecretTopsState;
  lucky: StoreSecretLuckyState;
  topic: StoreSecretTopicState;
  room: StoreSecretRoomState;
  privateRooms: StorePrivateRoomsState;
}

export default {
  namespaced: true,
  modules: {
    nav: navModule,
    filter: filterModule,
    list: listModule,
    post: postModule,
    search: searchModule,
    comment: commentModule,
    tops: topsModule,
    lucky: luckyModule,
    topic: topicModule,
    room: roomModule,
    privateRooms: privateRoomsModule,
  },
  state: {
    emotions: null,
    pendings: [],
    inputTips: null,
    stickyRules: null,
  },
  getters: {
    emotions: (state: StoreSecretState) => state.emotions || [],
    stickyRules: (state: StoreSecretState) => state.stickyRules || [],
  },
  actions: {
    [SECRET.GET_EMOTIONS](
      { commit, state }: StoreActionEnv<StoreSecretState>,
      { reload = false, silent = false }: { reload?: boolean; silent?: boolean } = {},
    ) {
      if (!state.emotions) {
        reload = true;
      }
      if (reload) {
        return new Promise((resolve, reject) => {
          const noPending = state.pendings.length === 0;
          commit(SECRET.GET_EMOTIONS, { resolve, reject });
          if (noPending) {
            api.getEmotions(silent).then((res) => {
              commit(SECRET.GET_EMOTIONS_SUCCESS, res.data);
            }).catch((err) => {
              commit(SECRET.GET_EMOTIONS_FAILURE, err);
            });
          }
        });
      }
      return Promise.resolve();
    },
    [SECRET.GET_INPUT_TIPS](
      { state, commit }: StoreActionEnv<StoreSecretState>,
      { reload = false }: { reload?: boolean } = {},
    ) {
      if (!state.inputTips || reload) {
        return new Promise<void>((resolve, reject) => {
          api.getInputTips().then((res) => {
            commit(SECRET.GET_INPUT_TIPS, res.data);
            resolve();
          }).catch(reject);
        });
      }
      return Promise.resolve();
    },
    [SECRET.GET_STICKY_RULES](
      { state, commit }: StoreActionEnv<StoreSecretState>,
      { reload = false }: { reload?: boolean } = {},
    ) {
      if (!state.stickyRules || reload) {
        return new Promise<void>((resolve, reject) => {
          api.getStickyRules().then((res) => {
            commit(SECRET.GET_STICKY_RULES, res.data);
            resolve();
          }).catch(reject);
        });
      }
      return Promise.resolve();
    },
  },
  mutations: {
    [SECRET.GET_EMOTIONS](state: StoreSecretState, data: { resolve: () => void; reject: (err: Error) => void }) {
      state.pendings.push(data);
    },
    [SECRET.GET_EMOTIONS_SUCCESS](state: StoreSecretState, data: SecretEmotion[]) {
      const pendings = state.pendings.splice(0);
      state.emotions = data;
      pendings.forEach(({ resolve }) => resolve());
    },
    [SECRET.GET_EMOTIONS_FAILURE](state: StoreSecretState, data: Error) {
      const pendings = state.pendings.splice(0);
      pendings.forEach(({ reject }) => reject(data));
    },
    [SECRET.GET_INPUT_TIPS](state: StoreSecretState, data: Record<string, string[]>) {
      state.inputTips = data;
    },
    [SECRET.GET_STICKY_RULES](state: StoreSecretState, data: string[]) {
      state.stickyRules = data;
    },
  },
};
